import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import type { DecklistCard } from "@liga/shared";
import { api } from "../api/client";
import { ErrorBox, Loading } from "../components/Loading";
import { usePageTitle } from "../hooks/usePageTitle";

/**
 * MTGGoldfish-style deck page for a single (event, player). The list on the
 * left is grouped by card type (Creature/Spell/Land/…) derived from the
 * Scryfall `type_line` we cached at import time; the panel on the right
 * shows a large preview that follows the user's hover/focus.
 */

const TYPE_BUCKETS: Array<{
  label: string;
  /** Lower-case substring match against Scryfall's `type_line`. */
  match: (typeLine: string) => boolean;
}> = [
  { label: "Companion", match: () => false }, // companion section has its own bucket
  { label: "Creature", match: (t) => t.includes("creature") },
  { label: "Planeswalker", match: (t) => t.includes("planeswalker") },
  { label: "Instant", match: (t) => t.includes("instant") },
  { label: "Sorcery", match: (t) => t.includes("sorcery") },
  { label: "Artifact", match: (t) => t.includes("artifact") },
  { label: "Enchantment", match: (t) => t.includes("enchantment") },
  { label: "Battle", match: (t) => t.includes("battle") },
  { label: "Land", match: (t) => t.includes("land") },
];

function classifyMainboard(card: DecklistCard): string {
  const tl = (card.typeLine ?? "").toLowerCase();
  if (!tl) return "Outras";
  for (const bucket of TYPE_BUCKETS) {
    if (bucket.label === "Companion") continue;
    if (bucket.match(tl)) return bucket.label;
  }
  return "Outras";
}

interface CardRowProps {
  card: DecklistCard;
  isActive: boolean;
  onHover: (card: DecklistCard) => void;
}

function CardRow({ card, isActive, onHover }: CardRowProps) {
  const cls = [
    "flex items-center gap-2 px-2 py-1 rounded-md text-sm cursor-default",
    "hover:bg-brand-50",
    isActive ? "bg-brand-50 text-ink" : "text-[#3f3a31]",
  ].join(" ");
  return (
    <li
      className={cls}
      onMouseEnter={() => onHover(card)}
      onFocus={() => onHover(card)}
      tabIndex={0}
    >
      <span className="font-display font-bold tabular-nums w-6 text-right text-[#6b6356]">
        {card.quantity}
      </span>
      <span className="truncate">{card.name}</span>
    </li>
  );
}

function CardGroup({
  title,
  total,
  cards,
  activeName,
  onHover,
}: {
  title: string;
  total: number;
  cards: DecklistCard[];
  activeName: string | null;
  onHover: (card: DecklistCard) => void;
}) {
  if (cards.length === 0) return null;
  return (
    <section className="space-y-1">
      <h3 className="text-[11px] uppercase tracking-[0.16em] text-[#8a8170] font-semibold flex items-baseline justify-between">
        <span>{title}</span>
        <span className="tabular-nums">{total}</span>
      </h3>
      <ul className="space-y-0.5">
        {cards.map((c) => (
          <CardRow
            key={`${c.section}-${c.sortOrder}-${c.name}`}
            card={c}
            isActive={activeName === c.name}
            onHover={onHover}
          />
        ))}
      </ul>
    </section>
  );
}

const CARD_BACK_IMG =
  "https://cards.scryfall.io/normal/back.png";

function CardPreview({ card }: { card: DecklistCard | null }) {
  const url = card?.imageNormalUrl ?? null;
  const alt = card?.name ?? "Decklist preview";
  return (
    <div className="rounded-2xl border border-line bg-white p-3 shadow-sm">
      <div className="aspect-[488/680] w-full overflow-hidden rounded-xl bg-[#f1ebde]">
        {url ? (
          <img
            src={url}
            alt={alt}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-center text-xs text-[#8a8170] px-4">
            {card
              ? "Sem imagem cacheada — passe o mouse em outra carta."
              : "Passe o mouse em uma carta para visualizar."}
          </div>
        )}
      </div>
      {card && (
        <div className="mt-3 px-1">
          <div className="font-display text-sm font-bold text-ink truncate">
            {card.name}
          </div>
          {card.typeLine && (
            <div className="text-xs text-[#8a8170] mt-0.5">{card.typeLine}</div>
          )}
        </div>
      )}
    </div>
  );
}

export function DecklistPage() {
  const params = useParams<{ eventId: string; resultId: string }>();
  const eventId = Number(params.eventId);
  const resultId = Number(params.resultId);

  const decklistQuery = useQuery({
    queryKey: ["decklist", eventId, resultId],
    queryFn: () => api.eventResultDecklist(eventId, resultId),
    enabled: Number.isFinite(eventId) && Number.isFinite(resultId),
  });

  const [previewCard, setPreviewCard] = useState<DecklistCard | null>(null);

  const grouped = useMemo(() => {
    const cards = decklistQuery.data?.cards ?? [];
    const main = cards.filter((c) => c.section === "mainboard");
    const side = cards.filter((c) => c.section === "sideboard");
    const companion = cards.filter((c) => c.section === "companion");

    const mainByBucket = new Map<string, DecklistCard[]>();
    for (const c of main) {
      const bucket = classifyMainboard(c);
      const arr = mainByBucket.get(bucket);
      if (arr) arr.push(c);
      else mainByBucket.set(bucket, [c]);
    }
    // Stable order: known buckets first, then any "Outras".
    const orderedMainGroups: Array<{
      label: string;
      total: number;
      cards: DecklistCard[];
    }> = [];
    for (const bucket of TYPE_BUCKETS) {
      const cs = mainByBucket.get(bucket.label);
      if (!cs || cs.length === 0) continue;
      orderedMainGroups.push({
        label: bucket.label,
        total: cs.reduce((a, c) => a + c.quantity, 0),
        cards: cs,
      });
      mainByBucket.delete(bucket.label);
    }
    for (const [label, cs] of mainByBucket) {
      orderedMainGroups.push({
        label,
        total: cs.reduce((a, c) => a + c.quantity, 0),
        cards: cs,
      });
    }

    return {
      orderedMainGroups,
      sideboard: side,
      sideboardTotal: side.reduce((a, c) => a + c.quantity, 0),
      companion,
      companionTotal: companion.reduce((a, c) => a + c.quantity, 0),
    };
  }, [decklistQuery.data]);

  const defaultPreview = useMemo(() => {
    const cards = decklistQuery.data?.cards ?? [];
    return (
      cards.find((c) => c.section === "mainboard" && c.imageNormalUrl) ??
      cards.find((c) => c.imageNormalUrl) ??
      cards[0] ??
      null
    );
  }, [decklistQuery.data]);

  const deckName = decklistQuery.data?.eventResult.deckName;
  const playerName = decklistQuery.data?.eventResult.playerName;
  const pageTitle = deckName
    ? `Decklist — ${deckName}${playerName ? ` (${playerName})` : ""}`
    : null;
  usePageTitle(pageTitle);

  if (decklistQuery.isLoading) return <Loading />;
  if (decklistQuery.error) {
    const err = decklistQuery.error as Error;
    if (/404/.test(err.message)) {
      return (
        <div className="space-y-6">
          <Link
            to={`/events/${eventId}`}
            className="text-sm font-medium text-brand-700 hover:underline"
          >
            ← Voltar para o evento
          </Link>
          <div className="rounded-2xl border border-line bg-white p-8 text-center text-[#6b6356]">
            <h2 className="font-display text-xl font-bold text-ink mb-2">
              Decklist indisponível
            </h2>
            <p className="text-sm">
              Esse evento foi importado sem a lista de cartas. Quando
              importado pelo melee.gg, a lista é capturada automaticamente.
            </p>
          </div>
        </div>
      );
    }
    return <ErrorBox error={err} />;
  }
  if (!decklistQuery.data) return <ErrorBox error={new Error("Decklist not found")} />;

  const data = decklistQuery.data;
  const totalMain = data.totals.mainboard;
  const totalSide = data.totals.sideboard;
  const activeCard = previewCard ?? defaultPreview;
  const activeName = activeCard?.name ?? null;

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <Link
          to={`/events/${eventId}`}
          className="text-sm font-medium text-brand-700 hover:underline"
        >
          ← Voltar para o evento
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-ink">
              {data.eventResult.deckName ?? "Decklist"}
            </h1>
            <p className="text-sm text-[#8a8170] mt-1">
              {data.eventResult.playerName} ·{" "}
              <span className="tabular-nums">#{data.eventResult.rank}</span> ·{" "}
              {data.eventResult.points} pts · {data.event.name} ({data.event.date})
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px]">
              {data.eventResult.subarchetypeName && (
                <span className="text-[#8a8170]">
                  {data.eventResult.subarchetypeName}
                </span>
              )}
              {data.eventResult.archetypeName && (
                <span className="inline-block rounded-full bg-brand-50 text-brand-900 font-semibold px-2 py-0.5">
                  {data.eventResult.archetypeName}
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#8a8170] font-semibold">
              Total
            </div>
            <div className="font-display text-2xl font-bold text-ink tabular-nums">
              {totalMain} <span className="text-[#b8b0a0] font-normal">/</span>{" "}
              {totalSide}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[#a39a87]">
              Main / Side
            </div>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6 items-start">
        <div className="rounded-2xl border border-line bg-white p-5">
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6"
            onMouseLeave={() => setPreviewCard(null)}
          >
            {grouped.orderedMainGroups.map((g) => (
              <CardGroup
                key={g.label}
                title={g.label}
                total={g.total}
                cards={g.cards}
                activeName={activeName}
                onHover={setPreviewCard}
              />
            ))}
            {grouped.companion.length > 0 && (
              <CardGroup
                title="Companion"
                total={grouped.companionTotal}
                cards={grouped.companion}
                activeName={activeName}
                onHover={setPreviewCard}
              />
            )}
            {grouped.sideboard.length > 0 && (
              <div className="md:col-span-2">
                <CardGroup
                  title="Sideboard"
                  total={grouped.sideboardTotal}
                  cards={grouped.sideboard}
                  activeName={activeName}
                  onHover={setPreviewCard}
                />
              </div>
            )}
          </div>
        </div>

        <aside className="lg:sticky lg:top-20">
          <CardPreview card={activeCard ?? null} />
          {data.meleeDecklistId && (
            <a
              href={`https://melee.gg/Decklist/View/${data.meleeDecklistId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 block text-center text-xs text-brand-700 hover:underline"
            >
              Ver no melee.gg ↗
            </a>
          )}
        </aside>
      </div>
      {/* Preload card backing image so the placeholder doesn't flash */}
      <img src={CARD_BACK_IMG} alt="" className="hidden" aria-hidden="true" />
    </div>
  );
}
